import {StyleSheet, css} from 'aphrodite';
import React, {useState} from 'react';

import {
  ARCHIVED_ANALYSIS_TEMPLATE_TOOLTIP_TEXT,
  PUBLISHED_ANALYSIS_TEMPLATE_TOOLTIP_TEXT,
} from 'analysis-tool/AnalysisToolConstants';
import {
  CanEditReturnValue,
  checkIsAnalysisArchived,
  showFlowchartEditFunctionality,
} from 'analysis-tool/EditAnalysisHelpers';
import {
  AnalysisFlowchartAction,
  AnalysisFlowchartAvailableAction,
} from 'analysis-tool/flowchart/actions/AnalysisFlowchartActionTypes';
import AnalysisFlowchart from 'analysis-tool/flowchart/AnalysisFlowchart';
import {AnalysisFlowchartMode} from 'analysis-tool/flowchart/AnalysisFlowchartContext';
import EditStepRightSidebar from 'analysis-tool/right-sidebar/components/EditStepRightSidebar';
import {RightSidebarWidth} from 'analysis-tool/right-sidebar/components/RightSidebarSkeleton';
import {AnalysisTemplateGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import BenchlingConfig from 'BenchlingConfig';

function getAvailableActions(
  setStepIdToEdit: (stepId: string) => void,
  analysisTemplate: AnalysisTemplateGraphQL
): Array<AnalysisFlowchartAvailableAction> {
  if (
    !showFlowchartEditFunctionality(analysisTemplate) ||
    !BenchlingConfig.ENABLE_ANALYSIS_TOOL_EDITABLE_TEMPLATES
  ) {
    return [];
  }

  return [
    {action: AnalysisFlowchartAction.ADD_SOURCE_STEP},
    {action: AnalysisFlowchartAction.DELETE_STEP},
    {action: AnalysisFlowchartAction.DELETE_STEP_AND_CHILDREN},
    {
      action: AnalysisFlowchartAction.OPEN_STEP_CONFIGURATION,
      onAction: ({stepId}) => {
        setStepIdToEdit(stepId);
      },
    } as AnalysisFlowchartAvailableAction<AnalysisFlowchartAction.OPEN_STEP_CONFIGURATION>,
  ];
}

export function checkIsAnalysisTemplatePublished(
  analysisTemplate: AnalysisTemplateGraphQL
): CanEditReturnValue | null {
  if (analysisTemplate.publishedAt != null) {
    return {
      canEdit: false,
      message: PUBLISHED_ANALYSIS_TEMPLATE_TOOLTIP_TEXT,
    };
  }
  return null;
}

export function checkCanModifyAnalysisTemplate(
  analysisTemplate: AnalysisTemplateGraphQL
): CanEditReturnValue {
  return (
    checkIsAnalysisArchived(analysisTemplate, ARCHIVED_ANALYSIS_TEMPLATE_TOOLTIP_TEXT) ??
    checkIsAnalysisTemplatePublished(analysisTemplate) ?? {canEdit: true, message: null}
  );
}

interface AnalysisFlowchartViewProps {
  analysisTemplate: AnalysisTemplateGraphQL;
}

/**
 * A flowchart representation of an analysisTemplate, as rendered full-screen in the template collections page.
 */
export default function AnalysisTemplateFlowchartView({
  analysisTemplate,
}: AnalysisFlowchartViewProps): JSX.Element {
  const [stepIdToEdit, setStepIdToEdit] = useState<string | null>(null);
  const stepToEdit = analysisTemplate.steps.find((step) => step.id === stepIdToEdit);

  return (
    <div className={css(styles.wrapper)}>
      <div
        className={css(styles.flowchart)}
        // `ReactFlow` is greedy for space, so must be given an explicit width to not crowd out the sidebar
        style={{width: stepToEdit == null ? '100%' : `calc(100% - ${RightSidebarWidth.WIDE}px)`}}
      >
        <AnalysisFlowchart
          analysis={analysisTemplate}
          mode={
            checkIsAnalysisTemplatePublished(analysisTemplate)
              ? AnalysisFlowchartMode.STATIC
              : AnalysisFlowchartMode.INTERACTIVE
          }
          availableActions={getAvailableActions(setStepIdToEdit, analysisTemplate)}
          canModifyAnalysis={() => checkCanModifyAnalysisTemplate(analysisTemplate)}
        />
      </div>
      {stepToEdit && (
        <EditStepRightSidebar
          pipeline={analysisTemplate}
          step={stepToEdit}
          onCancel={() => {
            setStepIdToEdit(null);
          }}
          onSave={() => {
            setStepIdToEdit(null);
          }}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    width: '100%',
    position: 'relative',
  },
  flowchart: {
    height: '100%',
  },
});
